import { useState } from 'react';
import { ReportStatus } from '@signalvote/shared';
import type { ComplianceReportDetailDto } from '@signalvote/shared';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useCampaignStore } from '@/stores';
import { usePeriodLockMutation } from '@/hooks/queries/useFinanceReports';
import { FilingLockCopy } from './filingLockCopy';

/**
 * ADR-019 D4 — the easy-but-audited reopen / re-lock affordance for a calendar-locked
 * reporting period. One low-friction action reopens a locked period (and one re-locks a
 * reopened one), each requiring a reason note — the audit trail is the safety, not the
 * friction. Gated on Finance/Manage (whoever can file can reopen), matching the
 * FileReport gate; the server's `[RequireCapability]` is the real boundary.
 *
 * The period is keyed by the report itself (`complianceReportId`); the server resolves
 * the correct period identity (the covering deadline for an in-system report, the report
 * for an externally-filed backfill — ADR-019 D4 / ADR-018).
 *
 * Context-aware copy: a never-filed locked period reopens clean ("file it now"); an
 * already-filed / externally-filed period reopens as an amendment. The client mirrors the
 * server's `willBeAmendment` rule (a `Filed` report ⇒ amendment) to choose the copy
 * before the call; the unlock response is the authority and the invalidated detail
 * refreshes the badge afterward.
 */
export function ReopenPeriodAffordance({
  report,
  initiallyExpanded = false,
}: {
  report: ComplianceReportDetailDto;
  initiallyExpanded?: boolean;
}) {
  const canManage = useCampaignStore((s) => s.canManage('Finance'));
  const { unlockPeriod, relockPeriod, isPending } = usePeriodLockMutation();
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Only whoever can file may reopen, and only a locked or reopened period has an
  // affordance (an open period needs none — it is freely editable already).
  if (!canManage) return null;
  if (!report.locked && !report.manuallyUnlocked) return null;
  // A superseded report's period belongs to the filing that replaced it.
  if (report.status === ReportStatus.Amended) return null;

  const isReopened = report.manuallyUnlocked;
  // Mirrors the server's willBeAmendment (`report.Status == Filed`): an already-filed
  // or externally-filed period (both `Filed`) reopens as an amendment; a never-filed
  // past-deadline period reopens clean. An amendment still being prepared is `Ready`, but its
  // period is on file through the original, so it reads as an amendment too.
  const willReopenAsAmendment = report.status === ReportStatus.Filed || report.isAmendment;

  const reset = () => {
    setExpanded(false);
    setReason('');
    setError(null);
  };

  const submit = async () => {
    if (!reason.trim()) {
      setError('Add a reason to reopen or re-lock this period.');
      return;
    }
    setError(null);
    try {
      const request = { filingDeadlineId: null, complianceReportId: report.id, reason: reason.trim() };
      if (isReopened) {
        await relockPeriod(request);
      } else {
        await unlockPeriod(request);
      }
      reset();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update the period lock.');
    }
  };

  if (!expanded) {
    return (
      <div data-testid="reopen-period-affordance">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setExpanded(true);
            setReason('');
            setError(null);
          }}
          data-testid={isReopened ? 'relock-period-button' : 'reopen-period-button'}
        >
          {isReopened ? FilingLockCopy.RelockButton : FilingLockCopy.ReopenButton}
        </Button>
      </div>
    );
  }

  return (
    <div
      className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3"
      data-testid="reopen-period-affordance"
    >
      {!isReopened && (
        <p className="text-sm text-amber-900" data-testid="reopen-context-message">
          {willReopenAsAmendment
            ? FilingLockCopy.ReopenAmendmentMessage
            : FilingLockCopy.ReopenNeverFiledMessage}
        </p>
      )}
      <div>
        <label htmlFor="reopen-reason" className="text-xs font-medium text-amber-900">
          {FilingLockCopy.ReopenReasonLabel}
        </label>
        <textarea
          id="reopen-reason"
          data-testid="reopen-reason-input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={FilingLockCopy.ReopenReasonPlaceholder}
          rows={2}
          className={cn(
            'mt-1 w-full rounded-md border px-2 py-1 text-sm',
            error && 'border-destructive',
          )}
        />
      </div>
      {error && (
        <p className="text-sm text-destructive" role="alert" data-testid="reopen-error">
          {error}
        </p>
      )}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={submit}
          disabled={isPending || !reason.trim()}
          data-testid="reopen-submit-button"
        >
          {isReopened ? FilingLockCopy.RelockSubmit : FilingLockCopy.ReopenSubmit}
        </Button>
        <Button variant="ghost" size="sm" onClick={reset} disabled={isPending}>
          {FilingLockCopy.ReopenCancel}
        </Button>
      </div>
    </div>
  );
}
